import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buyerLinks = [
  { id: "businesses", label: "Browse Businesses" },
  { id: "categories", label: "Categories" },
  { id: "how-it-works", label: "How It Works" },
  { id: "buyers", label: "Why Source With Us" },
];
const bizLinks = [
  { id: "businesses", label: "Join the Exchange" },
  { id: "why", label: "Benefits" },
  { id: "how-it-works", label: "How It Works" },
];
const companyLinks = [
  { id: "trust", label: "About Yuukke" },
  { id: "trust", label: "Our Impact" },
];

export default function Footer({ onNav }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-brand-ink text-brand-cream pt-20 pb-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center">
              <img src="/yuukke-logo-light.png" alt="Yuukke" className="h-9 w-auto brightness-0 invert" />
            </div>
            <p className="mt-4 text-brand-cream/70 text-[15px] leading-relaxed max-w-sm">
              Making women-led businesses discoverable, procurement-ready, and connected to real business opportunities.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Facebook, label: "Facebook" },
                { Icon: Instagram, label: "Instagram" },
                { Icon: Linkedin, label: "LinkedIn" },
                { Icon: Youtube, label: "YouTube" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-brand-cream/20 flex items-center justify-center hover:bg-brand-wine hover:border-brand-wine transition-colors"
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-cream/60">For Buyers</h4>
            <ul className="mt-4 space-y-2.5">
              {buyerLinks.map((l, i) => (
                <li key={i}>
                  <button onClick={() => onNav?.(l.id)} className="text-brand-cream/85 hover:text-brand-wine text-[15px] transition-colors" data-testid={`footer-buyer-${i}`}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-cream/60">For Businesses</h4>
            <ul className="mt-4 space-y-2.5">
              {bizLinks.map((l, i) => (
                <li key={i}>
                  <button onClick={() => onNav?.(l.id)} className="text-brand-cream/85 hover:text-brand-wine text-[15px] transition-colors" data-testid={`footer-biz-${i}`}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-cream/60">Company</h4>
            <ul className="mt-4 space-y-2.5">
              {companyLinks.map((l, i) => (
                <li key={i}>
                  <button onClick={() => onNav?.(l.id)} className="text-brand-cream/85 hover:text-brand-wine text-[15px] transition-colors" data-testid={`footer-company-${i}`}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-cream/60">Newsletter</h4>
            <p className="mt-4 text-brand-cream/75 text-[14px]">
              Stay updated on opportunities, events, and more.
            </p>
            <form onSubmit={subscribe} noValidate className="mt-4 relative" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full bg-brand-cream/[0.06] border border-brand-cream/20 rounded-full pl-4 pr-12 py-3 text-brand-cream placeholder:text-brand-cream/40 focus:outline-none focus:border-brand-wine transition-colors"
                data-testid="newsletter-email-input"
                aria-label="Email address"
              />
              <button
                type="submit"
                disabled={loading}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-brand-wine hover:bg-brand-wineDark text-white flex items-center justify-center transition-colors disabled:opacity-60"
                data-testid="newsletter-submit-btn"
                aria-label="Subscribe"
              >
                <Send size={15} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-brand-cream/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-brand-cream/60">
          <p>© {new Date().getFullYear()} Yuukke Business Exchange. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-brand-cream transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a href="#" className="hover:text-brand-cream transition-colors" data-testid="footer-terms">Terms & Conditions</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
